import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, messageErreur, telechargerDepuisPost } from '../../lib/api'
import { formatDateHeure } from '../../lib/format'
import { useTextePublic } from '../../lib/textes-publics'
import { SelecteurLangue } from '../../components/SelecteurLangue'
import { BandeauErreur, Button } from '../../ui'
interface BilletRemisPublic {
  programme: string
  dateHeure: string
  salle: string
  place?: string
  destinataire?: string
  porteur?: string | null
  statut?: string
}
function useJetonBillet() {
  const [jeton] = useState(
    () => new URLSearchParams(window.location.hash.slice(1)).get('jeton') ?? '',
  )
  useEffect(() => {
    if (window.location.hash)
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      )
  }, [])
  return jeton
}
export function PageBilletRemis({ transfert = false }: { readonly transfert?: boolean }) {
  const t = useTextePublic(),
    jeton = useJetonBillet(),
    [instance] = useState(() => crypto.randomUUID()),
    [confirme, setConfirme] = useState(false),
    base = transfert ? '/billets/transfert' : '/billets/remis',
    q = useQuery({
      queryKey: ['billet-remis', instance, base],
      queryFn: () => api.post<BilletRemisPublic>(base, { jeton }),
      enabled: !!jeton,
      retry: false,
    }),
    acceptation = useMutation({
      mutationFn: () => api.post('/billets/transfert/accepter', { jeton, confirmation: true }),
      onSuccess: () => {
        void q.refetch()
      },
    }),
    telechargement = useMutation({
      mutationFn: () => telechargerDepuisPost(base + '.pdf', 'billet.pdf', { jeton }),
    })
  return (
    <main className="mx-auto max-w-2xl p-5 space-y-4">
      <SelecteurLangue />
      <h1 className="text-xl font-semibold">
        {t.connu(transfert ? 'Recevoir un billet transféré' : 'Votre billet RidoMio')}
      </h1>
      {!jeton && <p>{t.connu('Ce lien est incomplet. Ouvrez le lien reçu dans le message.')}</p>}
      {q.isPending && jeton && <p>{t.connu('Lecture du billet…')}</p>}
      {(q.error || acceptation.error || telechargement.error) && (
        <BandeauErreur
          message={t.connu(messageErreur(q.error ?? acceptation.error ?? telechargement.error))}
        />
      )}{' '}
      {q.data && (
        <>
          <p>
            {q.data.programme} · {formatDateHeure(q.data.dateHeure)} · {q.data.salle}
          </p>
          <p>
            {q.data.destinataire ?? q.data.porteur} {q.data.place ?? ''}
          </p>
          {transfert && q.data.statut === 'propose' ? (
            <section className="space-y-3">
              <p>
                {t.connu(
                  'En acceptant, vous remplacez le QR initial. Le prix, la place et la facture d’origine restent inchangés.',
                )}
              </p>
              <label className="block">
                <input
                  type="checkbox"
                  checked={confirme}
                  onChange={(e) => setConfirme(e.target.checked)}
                />
                {t.connu('Je suis le destinataire et accepte de recevoir ce billet.')}
              </label>
              <Button
                disabled={!confirme || acceptation.isPending}
                onClick={() => acceptation.mutate()}
              >
                {t.connu('Accepter le transfert du billet')}
              </Button>
            </section>
          ) : (
            <>
              <output className="block">
                {t.connu(
                  transfert
                    ? 'Le transfert est accepté. L’ancien QR est révoqué.'
                    : 'Votre billet est disponible.',
                )}
              </output>
              <Button disabled={telechargement.isPending} onClick={() => telechargement.mutate()}>
                {t.connu('Télécharger le billet PDF')}
              </Button>
            </>
          )}
        </>
      )}
    </main>
  )
}
